import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Heart, Plus, Search, MapPin, Trash2, MessageCircle, Languages, Loader2 } from "lucide-react";

const CATEGORIES = ["Tous", "Logement", "Coloc", "Bons plans", "Soutien", "Évents", "Cours", "Général"];

// Translation languages — matches backend ALLOWED_TRANSLATE_LANGS
const TRANSLATE_LANGS = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
];

export default function Communaute() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("Tous");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", city: "", category: "Logement" });
  const [submitting, setSubmitting] = useState(false);

  const load = async (overrides = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const useCity = "city" in overrides ? overrides.city : city;
      const useCategory = "category" in overrides ? overrides.category : category;
      if (useCity && useCity.trim()) params.set("city", useCity.trim());
      if (useCategory && useCategory !== "Tous") params.set("category", useCategory);
      const { data } = await api.get(`/community/posts?${params.toString()}`);
      setPosts(data);
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Debounced auto-filter on city change (350ms) so the list updates as you type
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);

  // Re-fetch immediately when the active category pill changes
  const selectCategory = (c) => {
    setCategory(c);
    load({ category: c });
  };

  const hasActiveFilter = (city && city.trim()) || (category && category !== "Tous");
  const clearFilters = () => {
    setCity("");
    setCategory("Tous");
    load({ city: "", category: "Tous" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim() || !form.city.trim()) {
      toast.error("Titre, message et ville sont requis");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/community/posts", form);
      setPosts((p) => [data, ...p]);
      setOpen(false);
      setForm({ title: "", body: "", city: form.city, category: "Logement" });
      toast.success("Annonce publiée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publication impossible");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLike = async (id) => {
    try {
      const { data } = await api.post(`/community/posts/${id}/like`);
      setPosts((p) =>
        p.map((x) => {
          if (x.id !== id) return x;
          const nowLiked = data.liked;
          const liked_by = nowLiked
            ? Array.from(new Set([...(x.liked_by || []), user.id]))
            : (x.liked_by || []).filter((u) => u !== user.id);
          return { ...x, liked_by, likes: (x.likes || 0) + (nowLiked ? 1 : -1) };
        })
      );
    } catch {
      toast.error("Action impossible");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await api.delete(`/community/posts/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
      toast.success("Supprimée");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const filtered = useMemo(() => posts, [posts]);

  return (
    <div className="space-y-8" data-testid="community-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Entre étudiants</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">Communauté locale</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Mur d'annonces des étudiants : coloc, bons plans, soirées, soutien scolaire, urgences. Filtre par ville, like et publie ce dont tu as besoin.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-500 hover:bg-indigo-600" data-testid="open-new-post-btn">
              <Plus size={16} className="mr-2" /> Publier une annonce
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-post-dialog">
            <DialogHeader>
              <DialogTitle>Nouvelle annonce</DialogTitle>
              <DialogDescription>Visible par tous les étudiants. Reste respectueux et factuel.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label htmlFor="t">Titre</Label>
                <Input id="t" required maxLength={120} value={form.title}
                       onChange={(e) => setForm({ ...form, title: e.target.value })}
                       placeholder="Ex : Cherche coloc Lyon 7 — septembre"
                       className="mt-1.5" data-testid="post-title-input" />
              </div>
              <div>
                <Label htmlFor="b">Message</Label>
                <Textarea id="b" required maxLength={2000} rows={5} value={form.body}
                          onChange={(e) => setForm({ ...form, body: e.target.value })}
                          className="mt-1.5" data-testid="post-body-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="c">Ville</Label>
                  <Input id="c" required value={form.city}
                         onChange={(e) => setForm({ ...form, city: e.target.value })}
                         placeholder="Lyon, Paris, Toulouse..."
                         className="mt-1.5" data-testid="post-city-input" />
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="post-cat-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.filter((c) => c !== "Tous").map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-indigo-500 hover:bg-indigo-600" data-testid="submit-post-btn">
                  {submitting ? "Publication..." : "Publier"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Filtrer par ville (Lyon, Paris...)"
            className="pl-9"
            data-testid="filter-city-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => selectCategory(c)}
              data-testid={`filter-cat-${c}`}
              className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
                category === c
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        {hasActiveFilter && (
          <Button onClick={clearFilters} variant="outline" className="h-10" data-testid="clear-filters-btn">
            Réinitialiser
          </Button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-slate-500">Chargement...</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400" data-testid="community-empty">
          <MessageCircle size={32} className="mx-auto" />
          {hasActiveFilter ? (
            <>
              <p className="mt-3">Aucune annonce ne correspond à tes filtres.</p>
              <button
                onClick={clearFilters}
                className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 underline"
                data-testid="empty-clear-filters-btn"
              >
                Réinitialiser les filtres
              </button>
            </>
          ) : (
            <p className="mt-3">Aucune annonce. Sois le premier à publier !</p>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              currentUserId={user?.id}
              onLike={() => toggleLike(p.id)}
              onRemove={() => remove(p.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PostCard({ post: p, currentUserId, onLike, onRemove }) {
  const liked = (p.liked_by || []).includes(currentUserId);
  const isMine = p.user_id === currentUserId;
  const [translation, setTranslation] = useState(null);     // { lang, title, body } | null
  const [translating, setTranslating] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [langPickerOpen, setLangPickerOpen] = useState(false);

  const translate = async (code) => {
    setLangPickerOpen(false);
    if (translation && translation.lang === code && !showOriginal) {
      setShowOriginal(true);
      return;
    }
    setTranslating(true);
    try {
      const { data } = await api.post(`/community/posts/${p.id}/translate`, { lang: code });
      setTranslation({ lang: code, title: data.title, body: data.body });
      setShowOriginal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de traduction, réessaie.");
    } finally {
      setTranslating(false);
    }
  };

  const displayed = translation && !showOriginal ? translation : { title: p.title, body: p.body };
  const displayedLangMeta = translation ? TRANSLATE_LANGS.find((l) => l.code === translation.lang) : null;

  return (
    <li className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`post-${p.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-2 text-xs text-slate-500 mb-1">
            <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">{p.category}</span>
            <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.city}</span>
            <span>·</span>
            <span>{p.author_name}</span>
            <span>·</span>
            <span>{new Date(p.created_at).toLocaleDateString("fr-FR")}</span>
            {/* Discrete translate trigger */}
            <div className="relative inline-flex">
              <button
                type="button"
                onClick={() => setLangPickerOpen((v) => !v)}
                className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                aria-label="Traduire cette annonce"
                data-testid={`post-translate-btn-${p.id}`}
              >
                {translating ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
              </button>
              {langPickerOpen && (
                <div
                  className="absolute z-30 top-6 left-0 bg-white border border-slate-200 rounded-2xl py-2 shadow-lg w-44"
                  onMouseLeave={() => setLangPickerOpen(false)}
                  data-testid={`post-lang-picker-${p.id}`}
                >
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-3 pb-1">Traduire en…</p>
                  <div className="max-h-60 overflow-y-auto">
                    {TRANSLATE_LANGS.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => translate(l.code)}
                        className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-indigo-50 ${
                          translation && translation.lang === l.code ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-700"
                        }`}
                        data-testid={`post-lang-option-${p.id}-${l.code}`}
                      >
                        <span aria-hidden="true">{l.flag}</span>
                        <span>{l.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {translation && !showOriginal && displayedLangMeta && (
              <span className="inline-flex items-center gap-1 text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded-full" data-testid={`post-translated-badge-${p.id}`}>
                <span aria-hidden="true">{displayedLangMeta.flag}</span>
                <span>Traduit</span>
              </span>
            )}
          </div>
          <p className="font-display font-semibold text-slate-900 text-lg" data-testid={`post-title-${p.id}`}>{displayed.title}</p>
          <p className="text-slate-700 text-sm mt-1.5 whitespace-pre-line" data-testid={`post-body-${p.id}`}>{displayed.body}</p>
          {translation && (
            <button
              type="button"
              onClick={() => setShowOriginal((v) => !v)}
              className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 underline"
              data-testid={`post-toggle-original-${p.id}`}
            >
              {showOriginal ? "Voir la traduction" : "Voir l'original"}
            </button>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <button
            onClick={onLike}
            data-testid={`like-${p.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${
              liked ? "bg-red-50 border-red-200 text-red-600" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
            }`}
          >
            <Heart size={14} className={liked ? "fill-red-500 stroke-red-500" : ""} />
            <span>{p.likes || 0}</span>
          </button>
          {isMine && (
            <button
              onClick={onRemove}
              data-testid={`remove-post-${p.id}`}
              className="text-xs text-slate-400 hover:text-red-500 inline-flex items-center gap-1"
            >
              <Trash2 size={12} /> Supprimer
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
